import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Download, X, Smartphone, Monitor, CheckCircle } from 'lucide-react';

export const PWAInstallPrompt = ({ variant = 'banner' }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if dismissed recently (24 hours)
    const dismissedAt = localStorage.getItem('pwa_prompt_dismissed');
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      const hoursSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60);
      if (hoursSinceDismissed < 24) {
        setDismissed(true);
        return;
      }
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa_prompt_dismissed', Date.now().toString());
    setDismissed(true);
    setShowPrompt(false);
  };

  // Already installed - show success badge
  if (isInstalled) {
    return (
      <div className="flex items-center gap-2 text-green-600 text-sm bg-green-50 px-3 py-2 rounded-lg">
        <CheckCircle className="w-4 h-4" />
        <span>App installata</span>
      </div>
    );
  }

  // Dismissed or not available
  if (dismissed || !showPrompt) {
    return null;
  }

  // Banner variant (for login page)
  if (variant === 'banner') {
    return (
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-xl shadow-lg animate-in slide-in-from-bottom-4 duration-500">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-white/20 rounded-lg shrink-0">
            <Download className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base">Installa ServiCare</h3>
            <p className="text-blue-100 text-sm mt-0.5">
              Accedi più velocemente dalla schermata Home
            </p>
            <div className="flex items-center gap-3 mt-3">
              <Button 
                onClick={handleInstallClick}
                size="sm"
                className="bg-white text-blue-700 hover:bg-blue-50 font-medium shadow-sm"
                data-testid="pwa-install-btn"
              >
                <Download className="w-4 h-4 mr-1.5" />
                Installa App
              </Button>
              <button 
                onClick={handleDismiss}
                className="text-blue-200 hover:text-white text-sm underline"
              >
                Non ora
              </button>
            </div>
          </div>
          <button 
            onClick={handleDismiss}
            className="text-blue-200 hover:text-white p-1 shrink-0"
            aria-label="Chiudi"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Device icons */}
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/20 text-blue-100 text-xs">
          <div className="flex items-center gap-1.5">
            <Smartphone className="w-4 h-4" />
            <span>Mobile</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Monitor className="w-4 h-4" />
            <span>Desktop</span>
          </div>
          <span className="text-blue-200">• Funziona offline</span>
        </div>
      </div>
    );
  }

  // Compact variant (for other pages)
  return (
    <Button 
      onClick={handleInstallClick}
      variant="outline"
      size="sm"
      className="gap-2"
      data-testid="pwa-install-btn-compact"
    >
      <Download className="w-4 h-4" />
      Installa App
    </Button>
  );
};

export default PWAInstallPrompt;
